import dayjs from 'dayjs';
import {
  DaySequenceStatus,
  Job,
  JobApplicationStatus,
  JobStatus,
  ClassificationGroup,
  Classification,
} from '@core/generated/gql-types';

/** Query flag for the dashboard View all link onto today's rostered jobs. */
export const ACTIVE_ON_QUERY_PARAM = 'activeOn';

/** Status applied when opening Today's Jobs from the dashboard. */
export const TODAYS_ACTIVE_JOB_STATUSES: JobStatus[] = [JobStatus.Filled];

export const buildTodaysActiveJobsPath = (calendarDate: string) =>
  `/jobs?${ACTIVE_ON_QUERY_PARAM}=${calendarDate}`;
import { isJobScheduledOnCalendarDay } from '@core/job/workingDays/workingDays.util';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
} from '@core/util/jobScheduleBounds.util';
import {
  createFieldFilterConfig,
  type FilterConfig,
} from '@/components/filters';

export const getJobSiteLabel = (job: Job): string =>
  job.site?.name?.trim() || 'No site';

export type JobFilterConfig = FilterConfig<Job>;

export type JobFilterParams = {
  getClientById: (id: string) => { name?: string | null } | null;
  classificationGroups: ClassificationGroup[];
  getClassificationById: (id: string) => Classification | null;
  accountManagerByClientId: ReadonlyMap<string, string>;
};

export const getJobAccountManagerLabel = (
  clientId: string | null | undefined,
  accountManagerByClientId: ReadonlyMap<string, string>,
): string => {
  if (!clientId) return '';
  return accountManagerByClientId.get(clientId) ?? '';
};

export const getJobClassificationGroupName = (
  job: Job,
  params: Pick<
    JobFilterParams,
    'classificationGroups' | 'getClassificationById'
  >,
): string => {
  const classificationId = job.classification?.id ?? job.classificationId;
  if (!classificationId) return '-';
  const classification = params.getClassificationById(classificationId);
  if (!classification?.classificationGroupId) return '-';
  const group = params.classificationGroups.find(
    (g) => g.id === classification.classificationGroupId,
  );
  return group?.name || '-';
};

export const getJobClassificationName = (
  job: Job,
  params: Pick<JobFilterParams, 'getClassificationById'>,
): string => {
  const classificationId = job.classification?.id ?? job.classificationId;
  if (!classificationId) return '-';
  const classification = params.getClassificationById(classificationId);
  return classification?.name || '-';
};

const parseJobDateValue = (value: string | null | undefined) => {
  if (value == null || value === '') return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) {
    const fromEpoch = dayjs(Number(trimmed));
    return fromEpoch.isValid() ? fromEpoch : null;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed : null;
};

/** Every day of the week repeats within this many days of any start day. */
const DAYS_IN_WEEK = 7;

/**
 * True when the job's schedule intersects the inclusive calendar range and
 * the job has a working day on at least one day of that intersection.
 */
export const doesJobWorkInDateRange = (
  job: Job,
  rangeStart: Date,
  rangeEnd: Date,
): boolean => {
  const windowStart = dayjs(rangeStart).startOf('day');
  const windowEnd = dayjs(rangeEnd).startOf('day');

  const scheduleStart = getJobDisplayScheduleStartDay(job);
  const scheduleEnd = getJobDisplayScheduleEndDay(job) ?? scheduleStart;
  if (!scheduleStart || !scheduleEnd) {
    // Jobs without a schedule fall back to the day they were created.
    const createdAt = parseJobDateValue(job.createdAt);
    return (
      createdAt != null &&
      !createdAt.isBefore(windowStart, 'day') &&
      !createdAt.isAfter(windowEnd, 'day')
    );
  }

  const firstDay = scheduleStart.isAfter(windowStart)
    ? scheduleStart
    : windowStart;
  const lastDay = scheduleEnd.isBefore(windowEnd) ? scheduleEnd : windowEnd;

  for (
    let day = firstDay;
    !day.isAfter(lastDay, 'day') && day.diff(firstDay, 'day') < DAYS_IN_WEEK;
    day = day.add(1, 'day')
  ) {
    if (isJobScheduledOnCalendarDay(job, day, scheduleStart, scheduleEnd)) {
      return true;
    }
  }
  return false;
};

const FIELD_FILTER_CONFIG = [
  createFieldFilterConfig<Job>('status', 'Status', 'itemStatus'),
];

export const JOB_APPLICATION_FILTER_HAS = 'Has application';
export const JOB_APPLICATION_FILTER_NONE = 'No application';

export const JOB_APPLICATION_FILTER_OPTIONS = [
  JOB_APPLICATION_FILTER_HAS,
  JOB_APPLICATION_FILTER_NONE,
] as const;

/** PENDING application count for filters/UI; null when counts not loaded. */
export const getJobApplicationCount = (job: Job): number | null => {
  const countsByStatus = job.applicationCountsByStatus;
  if (countsByStatus == null) {
    return null;
  }
  const pending = countsByStatus.find(
    (entry) => entry.status === JobApplicationStatus.Pending,
  );
  if (pending == null) {
    return 0;
  }
  const numeric = Number(pending.count);
  return Number.isFinite(numeric) ? numeric : null;
};

export const getJobApplicationFilterValues = (job: Job): string[] => {
  const count = getJobApplicationCount(job);
  if (count == null) {
    return [];
  }
  if (count > 0) {
    return [JOB_APPLICATION_FILTER_HAS];
  }
  return [JOB_APPLICATION_FILTER_NONE];
};

export const jobMatchesApplicationFilters = (
  job: Job,
  selectedFilters: string[],
): boolean => {
  if (selectedFilters.length === 0) {
    return true;
  }
  const jobValues = getJobApplicationFilterValues(job);
  return jobValues.some((value) => selectedFilters.includes(value));
};

const DAY_SEQUENCE_STATUS_FILTER_OPTIONS = [
  DaySequenceStatus.InProgress,
  DaySequenceStatus.EscalatedToTenant,
  DaySequenceStatus.RequiresApproval,
  DaySequenceStatus.ProposedChange,
  DaySequenceStatus.ReadyForPay,
  DaySequenceStatus.PayProcessed,
].sort((a, b) => a.localeCompare(b));

export const createJobFilterConfig = (
  params: JobFilterParams,
): JobFilterConfig[] => {
  const {
    getClientById,
    classificationGroups,
    getClassificationById,
    accountManagerByClientId,
  } = params;
  const classificationParams = {
    classificationGroups,
    getClassificationById,
  };

  const daySequenceStatusFilterConfig: JobFilterConfig = {
    key: 'daySequenceStatus',
    title: 'Timesheet status',
    getOptions: () => DAY_SEQUENCE_STATUS_FILTER_OPTIONS,
    getValueFromItem: () => [],
  };

  const clientConfig: JobFilterConfig = {
    key: 'client',
    title: 'Client',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) => getClientById(job.clientId ?? '')?.name)
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort(),
    getValueFromItem: (job) => {
      const name = getClientById(job.clientId ?? '')?.name;
      return name ? [name] : [];
    },
  };

  const industryConfig: JobFilterConfig = {
    key: 'industry',
    title: 'Industry',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) =>
              getJobClassificationGroupName(job, classificationParams),
            )
            .filter((name) => name !== '-'),
        ),
      ).sort(),
    getValueFromItem: (job) => {
      const name = getJobClassificationGroupName(job, classificationParams);
      return name !== '-' ? [name] : [];
    },
  };

  const applicationConfig: JobFilterConfig = {
    key: 'application',
    title: 'Application',
    getOptions: () => [...JOB_APPLICATION_FILTER_OPTIONS],
    getValueFromItem: (job) => getJobApplicationFilterValues(job),
  };

  const skillSetConfig: JobFilterConfig = {
    key: 'skillSet',
    title: 'Skill set',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) =>
              getJobClassificationName(job, { getClassificationById }),
            )
            .filter((name) => name !== '-'),
        ),
      ).sort(),
    getValueFromItem: (job) => {
      const name = getJobClassificationName(job, { getClassificationById });
      return name !== '-' ? [name] : [];
    },
  };

  const accountManagerConfig: JobFilterConfig = {
    key: 'accountManager',
    title: 'Account manager',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) =>
              getJobAccountManagerLabel(job.clientId, accountManagerByClientId),
            )
            .filter((name) => name !== ''),
        ),
      ).sort((left, right) => left.localeCompare(right)),
    getValueFromItem: (job) => {
      const name = getJobAccountManagerLabel(
        job.clientId,
        accountManagerByClientId,
      );
      return name ? [name] : [];
    },
  };

  const siteConfig: JobFilterConfig = {
    key: 'site',
    title: 'Site',
    getOptions: (data: Job[]) =>
      Array.from(new Set((data || []).map(getJobSiteLabel))).sort(),
    getValueFromItem: (job) => [getJobSiteLabel(job)],
  };

  return [
    ...FIELD_FILTER_CONFIG,
    clientConfig,
    accountManagerConfig,
    siteConfig,
    industryConfig,
    skillSetConfig,
    applicationConfig,
    daySequenceStatusFilterConfig,
  ];
};

export const JOB_FILTER_KEYS = [
  ...FIELD_FILTER_CONFIG.map((f) => f.key),
  'client',
  'accountManager',
  'site',
  'industry',
  'skillSet',
  'application',
  'daySequenceStatus',
] as const;
